import { type ObjectEvent } from '@veriverse/shared';

/** Reacts to one kind of event from one object type (e.g. the calendar's `open`). */
export type ObjectEventHandler = (event: ObjectEvent) => void;

/**
 * Client side of object behaviors: `objectType:kind` → handler. Each interactive object type
 * registers what its events do on screen; unknown events are ignored.
 */
export function createObjectEventRouter() {
  const handlers = new Map<string, ObjectEventHandler>();
  return {
    on(objectType: string, kind: string, handler: ObjectEventHandler): () => void {
      const key = `${objectType}:${kind}`;
      handlers.set(key, handler);
      return () => {
        if (handlers.get(key) === handler) handlers.delete(key);
      };
    },
    dispatch(event: ObjectEvent): boolean {
      const handler = handlers.get(`${event.objectType}:${event.kind}`);
      handler?.(event);
      return handler !== undefined;
    },
  };
}

/** Reads an object's shared JSON value from the synced room state (`''` → `undefined`). */
export function parseObjectData<T>(data: string | undefined): T | undefined {
  if (!data) return undefined;
  try {
    return JSON.parse(data) as T;
  } catch {
    return undefined;
  }
}
